<template>
  <div
    :class="{
      'richtext-fi': true,
      'richtext-fi--light': isThemeLight,
      'richtext-fi--dark-bg': isBgDark,
    }"
  >
    <!-- eslint-disable vue/no-v-html -->
    <div
      v-if="inlineEditMode && edit !== null"
      class="richtext-fi__inline-editor fi:relative fi:cursor-pointer"
      @click="onOpenEditor"
    >
      <div
        class="richtext-fi--inline-edit fi:pointer-events-none"
        v-html="edit"
      />
    </div>
    <div
      v-else-if="!fischer.app.isEditMode() && customizedContent"
      ref="text"
      @click.capture="richtextClicked"
      v-html="customizedContent"
    />
    <!-- eslint-disable vue/no-v-html -->
    <div
      v-else-if="fischer.app.isEditMode() && (htmlContent?.editable || fieldValue?.editable)"
      v-html="htmlContent?.editable || fieldValue?.editable"
    />
    <!-- eslint-enable vue/no-v-html -->

    <slot />
  </div>
</template>

<script setup lang="ts">
import { useLoginStore } from 'store/login';
import { computed, inject, onBeforeUnmount, onMounted, ref, useTemplateRef, watch } from 'vue';

import { useBreakpoints } from '@/composables/breakpoint';
import { useFischer } from '@/composables/fischerPlugin';
import { useInlineEdit } from '@/composables/inlineEdit';
import type { MarkSsrOnly } from '@/composables/ssrOnly';
import type { FischerPlugin } from '@/lib/fischerPlugin';
import type { SingleLineTextField } from '@/sitecoreFieldTypes';
import smoothScroll from '@/utils/smoothScroll';

type Nullish<T> = {
  [P in keyof T]: T[P] | null | undefined;
};

defineOptions({
  name: 'RichtextFi',
});

const inhibitMarkSsrOnly = inject<MarkSsrOnly>('inhibitMarkSsrOnly');

const props = withDefaults(
  defineProps<{
    htmlContent?: SingleLineTextField | Nullish<SingleLineTextField> | null;
    isThemeLight?: boolean;
    isBgDark?: boolean;
    forceLoginOnStart?: boolean;
    fieldName?: string | null | undefined;
  }>(),
  {
    htmlContent: undefined,
    isThemeLight: false,
    isBgDark: false,
    fieldName: undefined,
  },
);

const { inlineEditMode, fieldValue, offerEditMode, notifyChanges, openEditor } = useInlineEdit(
  props.fieldName,
);

const onOpenEditor = async () => {
  const value = await openEditor();
  if (value !== null) {
    edit.value = value;
  }
};

const edit = ref<string | null>(null);
watch(
  () => edit.value,
  (newValue: string | null) => notifyChanges(newValue !== fieldValue.value?.value, newValue),
);

const loginStore = useLoginStore();
const fischer = useFischer();
const { windowDimensions } = useBreakpoints();

const textRef = useTemplateRef<HTMLDivElement>('text');
const tables = ref<null | ReturnType<Element['querySelectorAll']>>(null);

const dialogPages: (keyof FischerPlugin['pages'])[] = [
  'login',
  'registration',
  'passwordForgotten',
];

const customizedContent = computed<undefined | string>(() => {
  let content = props.htmlContent?.value || fieldValue.value?.value;
  if (!content) {
    return undefined;
  }
  // add container if table is used
  content = content.replace(
    /<table/g,
    '<div class="richtext-fi__table"><div class="richtext-fi__table__container"><table',
  );
  content = content.replace(/<\/table>/g, '</table></div></div>');

  // modifiy caption
  content = content.replace(/<caption/g, '<div class="richtext-fi__table__caption"');
  content = content.replace(/<\/caption>/g, '</div>');

  return content;
});
if (IS_SSR && inhibitMarkSsrOnly) {
  watch(customizedContent, (value) => {
    if (!value || !fischer.isLoginDialogEnabled()) {
      return;
    }
    dialogPages.forEach((dialogPage) => {
      const url = fischer.pages[dialogPage]().url;
      if (!url) {
        return;
      }
      const urlFull = `${fischer.hostUrlBase()}${url}`;
      if (value.includes(url) || value.includes(urlFull)) {
        inhibitMarkSsrOnly();
      }
    });
  });
}

const richtextClicked = (evt: MouseEvent) => {
  if (!fischer.isLoginDialogEnabled()) {
    return;
  }

  const target = (evt.target as HTMLAnchorElement)?.href;
  if (!target) {
    return;
  }
  dialogPages.forEach((dialogPage) => {
    const url = fischer.pages[dialogPage]().url;
    const urlFull = `${fischer.hostUrlBase()}${url}`;

    if (target === url || target === urlFull) {
      evt.preventDefault();
      evt.stopImmediatePropagation();
      console.debug(`opening ${dialogPage as string} dialog`);
      loginStore.triggerLogin({
        modal: dialogPage,
        forceLoginOnStart: props.forceLoginOnStart,
      });
    }
  });
};

const setTablesScrollable = () => {
  tables.value?.forEach((table) => {
    if (table.getElementsByTagName('table')[0].clientWidth > table.clientWidth) {
      table.classList.remove('richtext-fi__table--not-scrollable');
      table.classList.add('richtext-fi__table--scrollable');
    } else {
      table.classList.remove('richtext-fi__table--scrollable');
      table.classList.add('richtext-fi__table--not-scrollable');
    }
  });
};

const onTableScroll = (event: Event) => {
  const table = event.target as HTMLTableElement;
  const tableWidth = table.clientWidth;
  const scrollLeft = table.scrollLeft;
  const scrollWidth = table.scrollWidth - 20;
  if (tableWidth + scrollLeft >= scrollWidth) {
    table.parentElement?.classList.add('richtext-fi__table--scroll-end');
  } else {
    table.parentElement?.classList.remove('richtext-fi__table--scroll-end');
  }
};

watch(() => windowDimensions.value.width, setTablesScrollable);

onMounted(() => {
  if (fieldValue.value && !fieldValue.value.editable && offerEditMode.value) {
    edit.value = fieldValue.value.value || null;
  }

  if (!textRef.value) {
    return;
  }

  smoothScroll(textRef.value);

  tables.value = textRef.value?.querySelectorAll('div.richtext-fi__table');
  tables.value?.forEach((table) => {
    table
      .querySelector('.richtext-fi__table__container')
      ?.addEventListener('scroll', onTableScroll);
  });
  setTablesScrollable();
});

onBeforeUnmount(() => {
  tables.value?.forEach((table) => {
    table.removeEventListener('scroll', onTableScroll);
  });
});
</script>

<style src="./richtext-fi" />
